import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Loader2, Printer, ArrowLeft, ListChecks } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { KbStyleInjector } from "@/lib/kbSettings";
import { sanitizeKbHtml, useKbEmbedHosts } from "@/lib/kbSanitize";
import { enhanceKbContent } from "@/lib/kbContentRuntime";
import { useKbPrintPrep } from "@/lib/kbPrint";
import { KbPdfDialog } from "@/components/KbPdfDialog";

const articleAnchor = (slug) => `kb-pack-a-${slug}`;

/**
 * Iter 120 — Category print pack: a whole KB category rendered as one
 * booklet (cover + contents + every article, each starting on a new page).
 * Uses the same print stylesheet as single articles (#kb-print-root).
 */
export default function KBCategoryPrint() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const embedHosts = useKbEmbedHosts();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [excluded, setExcluded] = useState(new Set());
  useKbPrintPrep(data?.category?.name, "kb-print-root");

  useEffect(() => {
    api.get(`/kb/categories/${slug}/print`)
      .then(({ data: res }) => setData(res))
      .catch((e) => setError(e.response?.data?.detail || "Could not build the print pack"));
  }, [slug]);

  // Iter 303 — wire tabs, equations and copy buttons per article (own heading
  // ids so a contents block inside one article can't collide with another's).
  useEffect(() => {
    document.querySelectorAll(".kb-pack-article").forEach((art) => {
      const body = art.querySelector(".kb-prose");
      if (!body) return;
      body.querySelectorAll("h2, h3").forEach((h, i) => { if (!h.id) h.id = `${art.id}-h-${i}`; });
      enhanceKbContent(body, { glossary: false });
    });
  }, [data, excluded]);

  if (error) {
    return (
      <div className="kb-surface" data-testid="kb-print-pack-error">
        <p className="text-sm text-slate-500">{error}</p>
        <Button variant="outline" size="sm" className="mt-4" onClick={() => navigate(`/kb/c/${slug}`)}>
          <ArrowLeft className="h-4 w-4" /> Back to the category
        </Button>
      </div>
    );
  }
  if (!data) {
    return <div className="kb-surface"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>;
  }

  const { category, articles, total } = data;
  const included = articles.filter((a) => !excluded.has(a.article_id));
  const sections = [];
  included.forEach((a) => {
    const last = sections[sections.length - 1];
    if (!last || last.name !== a.section) sections.push({ name: a.section, items: [a] });
    else last.items.push(a);
  });
  const toggle = (id) => setExcluded((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  return (
    <div className="kb-surface" data-testid="kb-print-pack">
      <div className="mb-4 flex flex-wrap items-center gap-2 print:hidden">
        <Button variant="outline" size="sm" data-testid="kb-print-pack-back"
          onClick={() => navigate(`/kb/c/${slug}`)}>
          <ArrowLeft className="h-4 w-4" /> Back
        </Button>
        <Button size="sm" data-testid="kb-print-pack-print" onClick={() => window.print()}
          disabled={!included.length}
          className="bg-primary text-primary-foreground hover:bg-primary/90">
          <Printer className="h-4 w-4" /> Print booklet
        </Button>
        <KbPdfDialog />
        <span className="text-sm text-slate-500" data-testid="kb-print-pack-meta">
          {included.length} of {total} article{total === 1 ? "" : "s"} selected · the contents
          page links to each article in the saved PDF
        </span>
      </div>

      {/* Iter 120 — pick exactly what goes in the booklet before printing. */}
      <details className="mb-6 rounded-lg border border-slate-200 bg-white print:hidden" open
        data-testid="kb-pack-picker">
        <summary className="flex cursor-pointer items-center gap-2 px-4 py-3 text-sm font-medium text-slate-700">
          <ListChecks className="h-4 w-4 text-primary" /> Choose articles
          <span className="ml-auto flex gap-2">
            <button type="button" data-testid="kb-pack-select-all"
              onClick={(e) => { e.preventDefault(); setExcluded(new Set()); }}
              className="text-xs font-medium text-primary hover:underline">Select all</button>
            <button type="button" data-testid="kb-pack-select-none"
              onClick={(e) => { e.preventDefault(); setExcluded(new Set(articles.map((a) => a.article_id))); }}
              className="text-xs font-medium text-slate-500 hover:underline">Clear</button>
          </span>
        </summary>
        <ul className="max-h-72 divide-y divide-slate-100 overflow-y-auto border-t border-slate-100">
          {articles.map((a) => (
            <li key={a.article_id}>
              <label className="flex cursor-pointer items-center gap-3 px-4 py-2 text-sm hover:bg-slate-50"
                data-testid={`kb-pack-pick-${a.slug}`}>
                <input type="checkbox" checked={!excluded.has(a.article_id)}
                  onChange={() => toggle(a.article_id)} />
                <span className={`min-w-0 flex-1 truncate ${excluded.has(a.article_id) ? "text-slate-400 line-through" : "text-slate-700"}`}>
                  {a.name}
                </span>
                <span className="shrink-0 text-[11px] text-slate-400">{a.section}</span>
              </label>
            </li>
          ))}
        </ul>
      </details>

      {!included.length && (
        <p className="mb-6 text-sm text-slate-400 print:hidden" data-testid="kb-pack-empty">
          Nothing selected — tick at least one article to build the booklet.
        </p>
      )}

      <div id="kb-print-root">
        <KbStyleInjector />

        <section className="kb-pack-cover border-b border-slate-200 pb-8">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">
            Conduit Knowledge Base
          </p>
          <h1 className="mt-3 font-heading text-4xl font-bold text-slate-900" data-testid="kb-pack-title">
            {category.name}
          </h1>
          {category.description && <p className="mt-2 text-base text-slate-500">{category.description}</p>}
          <p className="mt-6 text-sm text-slate-500">
            {included.length} article{included.length === 1 ? "" : "s"} ·{" "}
            {new Date().toLocaleDateString([], { day: "numeric", month: "long", year: "numeric" })}
          </p>
        </section>

        <section className="kb-pack-contents mt-8" data-testid="kb-pack-contents">
          <h2 className="font-heading text-lg font-semibold text-slate-800">Contents</h2>
          <ol className="mt-4 space-y-4">
            {sections.map((s) => (
              <li key={s.name}>
                <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">{s.name}</p>
                <ul className="mt-1 space-y-1">
                  {s.items.map((a) => (
                    <li key={a.article_id} className="flex items-baseline gap-2 text-sm text-slate-700">
                      <span className="w-6 shrink-0 text-right text-slate-400">
                        {included.indexOf(a) + 1}.
                      </span>
                      <a href={`#${articleAnchor(a.slug)}`} className="kb-pack-toc-link flex-1 no-underline hover:text-primary hover:underline"
                        data-testid={`kb-pack-toc-${a.slug}`}
                        onClick={(e) => {
                          e.preventDefault();
                          document.getElementById(articleAnchor(a.slug))?.scrollIntoView({ behavior: "smooth", block: "start" });
                        }}>
                        {a.name}
                      </a>
                      <span className="text-xs text-slate-400">{a.word_count} words</span>
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ol>
        </section>

        {included.map((a, i) => (
          <article key={a.article_id} id={articleAnchor(a.slug)} className="kb-pack-article mt-10 border-t border-slate-200 pt-8"
            data-testid={`kb-pack-article-${a.slug}`}>
            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
              {i + 1} · {a.section}
            </p>
            <h2 className="mt-1 font-heading text-2xl font-bold text-slate-900">{a.name}</h2>
            <p className="mt-1 text-xs text-slate-400">Updated {a.updated}</p>
            <div className="kb-prose mt-4" dangerouslySetInnerHTML={{ __html: sanitizeKbHtml(a.body_html, embedHosts) }} />
          </article>
        ))}

        <div className="hidden border-t border-slate-300 pt-2 text-[9pt] text-slate-500 print:block">
          {typeof window !== "undefined" ? window.location.origin : ""}/kb/c/{slug} · printed{" "}
          {new Date().toLocaleDateString()}
        </div>
      </div>
    </div>
  );
}
